/**
 * Compact: a small player window. Everything in the menu bar is still one
 * click away under ☰; the window shows what is playing, the controls, and one
 * list at a time -- up next, the library, playlists, or flows.
 *
 * Flows play here too: pick one and Nova fills it from the library and starts
 * playing, no playlist needed.
 */

import { useEffect, useMemo, useState } from "react";
import { engineBase, type PlaylistItem } from "../api";
import { useApp } from "../core/app";
import { player, usePlayer, type QueueTrack } from "../core/player";
import { curateFlow, listFlows, loadFlow, type FlowSummary } from "../flow/flowApi";
import { Artwork } from "../shell/Artwork";
import { Brand, LayoutSwitch, showMenuBar } from "../shell/MenuBar";
import { Icon } from "../shell/Icons";
import { MoreButton, PopupMenu } from "../shell/PopupMenu";
import { QueueList } from "../shell/QueuePanel";
import { SearchField } from "../shell/SearchField";
import { Controls, SeekBar, Volume } from "../shell/Transport";
import { toast } from "../shell/Toast";
import { useMenus } from "../shell/menus";
import { trackMenu } from "../views/trackMenu";
import { usePref } from "../core/prefs";
import { songs } from "../format/count";

type Tab = "next" | "library" | "playlists" | "flows";

export async function playFlow(id: number, name: string) {
  const res = await loadFlow(id).catch(() => null);
  const spec = res?.flow?.spec;
  if (!spec) return toast(`Could not open “${name}”.`, "bad");
  const curated = await curateFlow({ spec, start: 0, until: spec.length, maxPicks: 120 });
  if (curated.error || !curated.picks.length) return toast(curated.error ?? `Nothing in the library fits “${name}” yet.`, "bad");
  const queue: QueueTrack[] = curated.picks.map((p) => ({
    id: p.trackId, title: p.title ?? "Untitled", artist: p.artist ?? "", album: null, bpm: p.bpm,
    musicalKey: p.camelot, lufs: null, cueIn: p.cueIn, cueOut: p.cueOut, duration: p.duration,
  }));
  player.playTracks(queue, 0);
  toast(`Playing “${name}” · ${songs(queue.length)} chosen from your library.`, "ok");
}

function HamburgerMenu() {
  const menus = useMenus();
  const [open, setOpen] = useState<DOMRect | null>(null);
  return (
    <>
      <button className="nv-iconbtn" aria-label="Menu" title="Menu" aria-haspopup="menu" aria-expanded={!!open} onClick={(e) => setOpen(open ? null : e.currentTarget.getBoundingClientRect())}>
        <Icon name="list" />
      </button>
      {open && (
        <PopupMenu
          label="Menu"
          anchor={{ rect: open }}
          onClose={() => setOpen(null)}
          items={menus.map((m) => ({ id: m.id, label: m.label, submenu: m.items }))}
        />
      )}
    </>
  );
}

function CompactLibrary() {
  const app = useApp();
  const p = usePlayer();
  const tracks = app.library.tracks;
  if (!tracks.length) return <div className="nv-empty">No songs{app.input ? " match" : " yet"}.</div>;
  return (
    <ol className="nv-queue compact">
      {tracks.slice(0, 300).map((t, i) => (
        <li key={t.id} className={p.current?.track.id === t.id ? "on" : ""} onDoubleClick={() => player.playTracks(tracks, i)}>
          <Artwork trackId={t.id} seed={t.album || t.artist || t.title} size={34} />
          <div className="nv-queue-text" onClick={() => player.playTracks(tracks, i)}>
            <b title={t.title}>{t.title}</b>
            <span title={t.artist}>{t.artist}</span>
          </div>
          <span className="nv-queue-meta">{t.bpm != null ? Math.round(t.bpm) : "—"}{t.musicalKey && t.musicalKey !== "ambiguous" ? ` · ${t.musicalKey}` : ""}</span>
          <MoreButton label={`Options for ${t.title}`} items={() => trackMenu(app, [t], tracks)} />
        </li>
      ))}
    </ol>
  );
}

function CompactPlaylists() {
  const app = useApp();
  if (!app.library.playlists.length) return <div className="nv-empty">No playlists yet.</div>;
  return (
    <ol className="nv-queue compact">
      {app.library.playlists.map((pl) => (
        <li key={pl.id}>
          <span className="nv-art" style={{ width: 34, height: 34, borderRadius: 6 }}><Icon name="list" /></span>
          <div className="nv-queue-text" onClick={async () => {
            const body = await fetch(`${engineBase()}/api/playlists?id=${pl.id}`).then((r) => r.json()).catch(() => ({}));
            const items: PlaylistItem[] = body.playlist?.items ?? [];
            const queue: QueueTrack[] = items.filter((x) => x.type === "song" && x.trackId && !x.missing).map((x) => ({
              id: String(x.trackId), title: x.title ?? "Untitled", artist: x.artist ?? "", album: null, bpm: x.bpm,
              musicalKey: x.camelot, lufs: null, cueIn: null, cueOut: null, duration: x.duration ?? 0,
            }));
            if (queue.length) player.playTracks(queue);
          }}>
            <b>{pl.name}</b>
            <span>{pl.items} items</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

function CompactFlows() {
  const app = useApp();
  const [flows, setFlows] = useState<FlowSummary[] | null>(null);
  useEffect(() => void listFlows().then(setFlows), []);
  if (!flows) return <div className="nv-empty">Loading…</div>;
  if (!flows.length) return <div className="nv-empty">No flows saved yet. Draw one in the Pro layout (Tools → Flow).</div>;
  return (
    <ol className="nv-queue compact">
      {flows.map((f) => (
        <li key={f.id}>
          <span className="nv-art nv-flow-icon" style={{ width: 34, height: 34, borderRadius: 6 }}><Icon name="wave" /></span>
          <div className="nv-queue-text" onClick={() => void playFlow(f.id, f.name)} title="Play this flow, filled from your library">
            <b>{f.name}</b>
            <span>Tap to play · chosen fresh each time</span>
          </div>
          <MoreButton label={`Options for ${f.name}`} items={[
            { id: "play", label: "Play", icon: "play", run: () => playFlow(f.id, f.name) },
            { id: "edit", label: "Edit in Pro layout", icon: "edit", run: () => { app.setLayout("pro"); app.navigate("flow", { id: String(f.id) }); } },
          ]} />
        </li>
      ))}
    </ol>
  );
}

export function CompactLayout() {
  const app = useApp();
  const menus = useMenus();
  const p = usePlayer();
  const [tab, setTab] = usePref<Tab>("nova.compact.tab", "next");
  const t = p.current?.track;
  const tabs = useMemo<{ id: Tab; label: string }[]>(() => [
    { id: "next", label: "Up next" }, { id: "library", label: "Library" }, { id: "playlists", label: "Playlists" }, { id: "flows", label: "Flows" },
  ], []);

  return (
    <div className="nv-app nv-compact">
      <header className="nv-compact-head">
        {showMenuBar() && <HamburgerMenu />}
        <Brand />
        <span className="nv-spacer" />
        <LayoutSwitch />
      </header>
      <div className="nv-compact-search">
        <SearchField compact placeholder="Search…" />
        <MoreButton label="More" items={() => [
          ...(showMenuBar() ? [] : menus.map((m) => ({ id: `menu-${m.id}`, group: "0", label: m.label, submenu: m.items }))),
          { id: "settings", label: "Settings…", icon: "settings", run: () => app.openSettings("general") },
          { id: "theme", label: app.theme.resolved === "dark" ? "Light theme" : "Dark theme", icon: app.theme.resolved === "dark" ? "sun" : "moon", run: () => app.theme.setChoice(app.theme.resolved === "dark" ? "light" : "dark") },
        ]} />
      </div>
      <section className="nv-compact-now" aria-label="Now playing">
        <Artwork trackId={t?.id} seed={t ? t.album || t.artist || t.title : "nova"} size={84} rounded={10} />
        <div className="nv-compact-text">
          <small>{p.playing ? "Now playing" : t ? "Paused" : "Nothing playing"}</small>
          <b title={t?.title}>{t?.title ?? "Pick something to play"}</b>
          <span title={t?.artist}>{t ? [t.artist, t.album].filter(Boolean).join(" · ") : "Your library, playlists and flows are below"}</span>
          {t && (
            <div className="nv-compact-meta">
              {t.bpm != null && <span className="nv-pill">{Math.round(t.bpm)} BPM</span>}
              {t.musicalKey && t.musicalKey !== "ambiguous" && <span className="nv-pill">{t.musicalKey}</span>}
            </div>
          )}
        </div>
      </section>
      <div className="nv-compact-transport">
        <SeekBar height={30} />
        <Controls big />
        <Volume wide />
      </div>
      <nav className="nv-tabs nv-compact-tabs" role="tablist">
        {tabs.map((x) => (
          <button key={x.id} role="tab" aria-selected={tab === x.id} className={tab === x.id ? "on" : ""} onClick={() => setTab(x.id)}>
            {x.label}
          </button>
        ))}
      </nav>
      <div className="nv-compact-list">
        {tab === "next" ? <QueueList compact /> : tab === "library" ? <CompactLibrary /> : tab === "playlists" ? <CompactPlaylists /> : <CompactFlows />}
      </div>
      <div className="nv-status compact">
        <span className={`nv-led ${app.offline ? "bad" : "ok"}`} />
        <span>{app.offline ? "Engine offline" : songs(app.library.total)}</span>
        <span className="nv-spacer" />
        <span>Compact</span>
      </div>
    </div>
  );
}
